
import {chain, isUndefined, map, sortBy, values} from 'lodash';

import {groupByMultipleProperties} from '../../../../utils/common-util.js';
import {DateUtil} from '../../../../utils/date-util.js';
import {
    getAggregationDateOrTimeStampString,
    NO_OF_CHANGED_FARES_KEY
} from '../charts-util.js';
import {
    SumAndAverageCalculation,
    getFareOrFareChangeValue,
    getMinAvgMaxValue,
    getRespectivePercentKey
} from '../chart-competition-fare-changes/chart-competition-fare-changes-util.js';
import {DASHBOARD_TOP_TABS_VALUE} from '../../../../utils/constants.js';

const AVG_FARE_CHANGE_AMT = 'avgFareChangeAmt';
const AVG_FARE_CHANGE_PERCENT = 'avgFareChangePercent';
const FCA = 'fareChangeAmount'; // fare changes in amount
const FCP = 'fareChangePercentage'; // fare changes in percentage

export const GROUP_BY_FIELDS = ['carrierCode', 'origin', 'destination'];

export const getCompetitionFareChangesConfigColumns = (filterBy) => {
    const columnsConfig = [
        {
            key: 'carrierCode',
            label: 'Carrier Code',
            Header: 'Carrier Code',
            accessor: 'carrierCode',
            thClassName: 'text-align-center',
            tdClassName: 'cell-market-region',
            tooltip: {
                isShowTooltip: true,
                isShowRowValues: true
            }
        },
        {
            key: 'origin',
            label: 'Origin',
            Header: 'Origin',
            accessor: 'origin',
            thClassName: 'text-align-center',
            tdClassName: 'cell-market-region',
            tooltip: {
                isShowTooltip: true,
                isShowRowValues: true
            }
        },
        {
            key: 'destination',
            label: 'Destination',
            Header: 'Destination',
            accessor: 'destination',
            thClassName: 'text-align-center',
            tdClassName: 'cell-market-region',
            tooltip: {
                isShowTooltip: true,
                isShowRowValues: true
            }
        },
        {
            key: 'season',
            label: 'Season',
            Header: 'Season',
            accessor: 'season',
            thClassName: 'text-align-center',
            tdClassName: 'cell-market-region',
            tooltip: {
                isShowTooltip: true,
                isShowRowValues: true
            }
        }
    ];

    filterBy && filterBy.rbd && map(filterBy.rbd, eachRbd => {
        columnsConfig.push({
            key: `${FCA}_${eachRbd}`,
            label: `${eachRbd}`,
            Header: `${eachRbd}`,
            accessor: `${FCA}_${eachRbd}`,
            thClassName: 'text-align-center',
            tdClassName: 'text-align-center',
            tooltip: {
                isShowTooltip: true,
                isShowRowValues: true
            }
        });
    });

    filterBy && filterBy.rbd && map(filterBy.rbd, eachRbd => {
        columnsConfig.push({
            key: `${FCP}_${eachRbd}`,
            label: `${eachRbd}%`,
            Header: `${eachRbd}%`,
            accessor: `${FCP}_${eachRbd}`,
            thClassName: 'text-align-center',
            tdClassName: 'text-align-center',
            tooltip: {
                isShowTooltip: true,
                isShowRowValues: true
            }
        });
    });

    columnsConfig.push({
        key: NO_OF_CHANGED_FARES_KEY,
        label: 'No. of Fare Change',
        Header: 'No. of Fare Change',
        accessor: NO_OF_CHANGED_FARES_KEY,
        thClassName: 'text-align-center',
        tdClassName: 'cell-market-region',
        tooltip: {
            isShowTooltip: true,
            isShowRowValues: true
        }
    });

    columnsConfig.push({
        key: AVG_FARE_CHANGE_AMT,
        label: 'Avg. Fare Change Amt',
        Header: 'Avg. Fare Change Amt',
        accessor: AVG_FARE_CHANGE_AMT,
        thClassName: 'text-align-center',
        tdClassName: 'cell-market-region',
        tooltip: {
            isShowTooltip: true,
            isShowRowValues: true
        }
    });

    columnsConfig.push({
        key: AVG_FARE_CHANGE_PERCENT,
        label: 'Avg. Fare Change %',
        Header: 'Avg. Fare Change %',
        accessor: AVG_FARE_CHANGE_PERCENT,
        thClassName: 'text-align-center',
        tdClassName: 'cell-market-region',
        tooltip: {
            isShowTooltip: true,
            isShowRowValues: true
        }
    });

    columnsConfig.push({
        key: 'currencyCode',
        label: 'Currency',
        Header: 'Currency',
        accessor: 'currencyCode',
        thClassName: 'text-align-center',
        tdClassName: 'cell-market-region',
        tooltip: {
            isShowTooltip: true,
            isShowRowValues: true
        }
    });

    return columnsConfig;
};

export const getCompetitionFareChangesHeatMapColumns = (filterBy) => {
    const columnsConfig = [];
    filterBy && filterBy.rbd && map(filterBy.rbd, eachRbd => {
        columnsConfig.push(`${FCP}_${eachRbd}`);
    });

    return columnsConfig;
};

export const getFareChangesGridData = (fareChangesSummaryList, pickedFareChange, showFaresBy) => {
    const sumAndAverageCalculation = new SumAndAverageCalculation({isChangeFareGrid: true});
    let transformedData = {};
    let tableColumns = [];
    const respectivePercentKey = getRespectivePercentKey(showFaresBy);

    map(fareChangesSummaryList, fc => {
        if (isUndefined(fc[pickedFareChange]) || isUndefined(fc[`${pickedFareChange}Changes`])) {
            return;
        }

        const fareValue = getFareOrFareChangeValue(fc, pickedFareChange, showFaresBy);
        const fareValuePercent = getFareOrFareChangeValue(fc, pickedFareChange, respectivePercentKey);

        const recordKey = getAggregationDateOrTimeStampString(fc, DASHBOARD_TOP_TABS_VALUE.COMPETITION, DateUtil.DATE_FORMATS.YEAR_MONTH_DAY_TIME);
        fc.dateValue = recordKey;

        const fcaRBDKey = `${FCA}_${fc.rbd}`;
        const fcpRBDKey = `${FCP}_${fc.rbd}`;
        const fcaCarrierRBDKey = `${fcaRBDKey}_${fc.carrierCode}`;
        const fcpCarrierRBDKey = `${fcpRBDKey}_${fc.carrierCode}`;
        const fcaCarrierOriRBDKey = `${fcaRBDKey}_${fc.carrierCode}_${fc.origin}`;
        const fcpCarrierOriRBDKey = `${fcpRBDKey}_${fc.carrierCode}_${fc.origin}`;
        const fcaCarrierOriDesRBDKey = `${fcaRBDKey}_${fc.carrierCode}_${fc.origin}_${fc.destination}`;
        const fcpCarrierOriDesRBDKey = `${fcpRBDKey}_${fc.carrierCode}_${fc.origin}_${fc.destination}`;
        const dataKey = `${fc.carrierCode}_${fc.origin}_${fc.destination}_${fc.season}`;

        sumAndAverageCalculation.addToSum(fc, pickedFareChange, showFaresBy);
        sumAndAverageCalculation.addToSum(fc, pickedFareChange, respectivePercentKey);
        sumAndAverageCalculation.addToSum(fc, `${pickedFareChange}Changes`, NO_OF_CHANGED_FARES_KEY);

        if (isUndefined(transformedData[recordKey])) {
            transformedData[recordKey] = {};
        }

        if (isUndefined(transformedData[recordKey][dataKey])) {
            transformedData[recordKey][dataKey] = {
                dataKey: dataKey,
                carrierCode: fc.carrierCode,
                origin: fc.origin,
                destination: fc.destination,
                season: fc.season,
                currencyCode: fc.currencyCode,
                [fcaRBDKey]: fareValue,
                [fcpRBDKey]: fareValuePercent,
                [fcaCarrierRBDKey]: fareValue,
                [fcpCarrierRBDKey]: fareValuePercent,
                [fcaCarrierOriRBDKey]: fareValue,
                [fcpCarrierOriRBDKey]: fareValuePercent,
                [fcaCarrierOriDesRBDKey]: fareValue,
                [fcpCarrierOriDesRBDKey]: fareValuePercent,
                noOfChangesPopUpData: fc[`${pickedFareChange}Changes`].fares
            };
        } else {
            transformedData[recordKey][dataKey] = {
                ...transformedData[recordKey][dataKey],
                noOfChangesPopUpData: [...transformedData[recordKey][dataKey].noOfChangesPopUpData, ...fc[`${pickedFareChange}Changes`].fares],
                [fcaRBDKey]: getMinAvgMaxValue(transformedData[recordKey][dataKey], fcaRBDKey, showFaresBy, fareValue),
                [fcpRBDKey]: getMinAvgMaxValue(transformedData[recordKey][dataKey], fcpRBDKey, showFaresBy, fareValuePercent),
                [fcaCarrierRBDKey]: getMinAvgMaxValue(transformedData[recordKey][dataKey], fcaCarrierRBDKey, showFaresBy, fareValue),
                [fcpCarrierRBDKey]: getMinAvgMaxValue(transformedData[recordKey][dataKey], fcpCarrierRBDKey, showFaresBy, fareValuePercent),
                [fcaCarrierOriRBDKey]: getMinAvgMaxValue(transformedData[recordKey][dataKey], fcaCarrierOriRBDKey, showFaresBy, fareValue),
                [fcpCarrierOriRBDKey]: getMinAvgMaxValue(transformedData[recordKey][dataKey], fcpCarrierOriRBDKey, showFaresBy, fareValuePercent),
                [fcaCarrierOriDesRBDKey]: getMinAvgMaxValue(transformedData[recordKey][dataKey], fcaCarrierOriDesRBDKey, showFaresBy, fareValue),
                [fcpCarrierOriDesRBDKey]: getMinAvgMaxValue(transformedData[recordKey][dataKey], fcpCarrierOriDesRBDKey, showFaresBy, fareValuePercent)
            };
        }
        tableColumns = [...tableColumns, fcaRBDKey, fcpRBDKey];
    });

    tableColumns = chain(tableColumns)
        .uniq()
        .compact()
        .sort()
        .value();

    const dataForDataGrid = {};
    map(transformedData, (eachDataByDate, eachDateKey) => {
        map(eachDataByDate, (eachRow, eachKey) => {
            eachRow[NO_OF_CHANGED_FARES_KEY] = sumAndAverageCalculation.getSum(`${eachDateKey}_${eachKey}_${NO_OF_CHANGED_FARES_KEY}`).sum;
            eachRow[AVG_FARE_CHANGE_AMT] = sumAndAverageCalculation.getAvg(`${eachDateKey}_${eachKey}_${showFaresBy}`);
            eachRow[AVG_FARE_CHANGE_PERCENT] = sumAndAverageCalculation.getAvg(`${eachDateKey}_${eachKey}_${respectivePercentKey}`);

            return eachRow;
        });

        dataForDataGrid[eachDateKey] = values(groupByMultipleProperties(sortBy(values(eachDataByDate), ['dataKey']), GROUP_BY_FIELDS));
        return eachDataByDate;
    });

    setAvgAndGroupedRowLevelCounts(dataForDataGrid, tableColumns, GROUP_BY_FIELDS);

    return {
        transformedData: dataForDataGrid,
        tableColumns
    };
};

const setCountOnRowGroup = (assignTo, rowGroup, fields, assignFrom) => {

    map(fields, eachPrefix => {
        const eachPrefixPlusField = `${eachPrefix}_${rowGroup}`;

        if (assignFrom[eachPrefixPlusField]) {
            if (assignTo[eachPrefix]) {
                if (assignTo[eachPrefix] > assignFrom[eachPrefixPlusField]) {
                    assignTo[eachPrefix] = assignFrom[eachPrefixPlusField];
                }
            } else {
                assignTo[eachPrefix] = assignFrom[eachPrefixPlusField];
            }
        }
    });
};

const addValue = (from, to, valueOfKey) => {
    return from[valueOfKey] ? (to[valueOfKey] ? (to[valueOfKey] + from[valueOfKey]) : from[valueOfKey]) : to[valueOfKey];
};

const setAvgAndGroupedRowLevelCounts = (groupedCollections, columnIndexes, keys) => {
    // TODO: the depth of the map could have been made dynamic
    map(groupedCollections, gc1 => {

        map(gc1, gc2 => {
            let tooltipTitle = '';
            const carrierCode = gc2[keys[0]];
            const avgCarrierCode = new SumAndAverageCalculation();

            map(gc2.subRows, gc3 => {
                const carrierOrg = `${carrierCode}_${gc3[keys[1]]}`;
                const avgCarrierOrg = new SumAndAverageCalculation();

                map(gc3.subRows, gc4 => {
                    const carrierOrgDes = `${carrierOrg}_${gc4[keys[2]]}`;
                    tooltipTitle = carrierOrgDes;
                    const avgCarrierOrgDes = new SumAndAverageCalculation();

                    map(gc4.subRows, gc5 => {
                        gc5['tooltipTitle'] = tooltipTitle;
                        gc4.currencyCode = gc5.currencyCode;
                        setCountOnRowGroup(gc4, carrierOrgDes, columnIndexes, gc5);
                        setCountOnRowGroup(gc3, carrierOrg, columnIndexes, gc5);
                        setCountOnRowGroup(gc2, carrierCode, columnIndexes, gc5);

                        gc4[NO_OF_CHANGED_FARES_KEY] = addValue(gc5, gc4, NO_OF_CHANGED_FARES_KEY);
                        gc3[NO_OF_CHANGED_FARES_KEY] = addValue(gc5, gc3, NO_OF_CHANGED_FARES_KEY);
                        gc2[NO_OF_CHANGED_FARES_KEY] = addValue(gc5, gc2, NO_OF_CHANGED_FARES_KEY);

                        avgCarrierOrgDes.addToKey(`${FCA}_${carrierOrgDes}`, gc5[AVG_FARE_CHANGE_AMT]);
                        avgCarrierOrgDes.addToKey(`${FCP}_${carrierOrgDes}`, gc5[AVG_FARE_CHANGE_PERCENT]);

                        delete gc5.carrierCode;
                        delete gc5.origin;
                        delete gc5.destination;
                        delete gc5.currencyCode;
                    });

                    gc4['tooltipTitle'] = tooltipTitle;
                    gc4[AVG_FARE_CHANGE_AMT] = avgCarrierOrgDes.getAvg(`${FCA}_${carrierOrgDes}`);
                    gc4[AVG_FARE_CHANGE_PERCENT] = avgCarrierOrgDes.getAvg(`${FCP}_${carrierOrgDes}`);

                    avgCarrierOrg.addToKey(`${FCA}_${carrierOrg}`, gc4[AVG_FARE_CHANGE_AMT]);
                    avgCarrierOrg.addToKey(`${FCP}_${carrierOrg}`, gc4[AVG_FARE_CHANGE_PERCENT]);
                });

                gc3['tooltipTitle'] = tooltipTitle;
                gc3[AVG_FARE_CHANGE_AMT] = avgCarrierOrg.getAvg(`${FCA}_${carrierOrg}`);
                gc3[AVG_FARE_CHANGE_PERCENT] = avgCarrierOrg.getAvg(`${FCP}_${carrierOrg}`);

                avgCarrierCode.addToKey(`${FCA}_${carrierCode}`, gc3[AVG_FARE_CHANGE_AMT]);
                avgCarrierCode.addToKey(`${FCP}_${carrierCode}`, gc3[AVG_FARE_CHANGE_PERCENT]);
            });

            gc2['tooltipTitle'] = tooltipTitle;
            gc2[AVG_FARE_CHANGE_AMT] = avgCarrierCode.getAvg(`${FCA}_${carrierCode}`);
            gc2[AVG_FARE_CHANGE_PERCENT] = avgCarrierCode.getAvg(`${FCP}_${carrierCode}`);
        });
    });


    return groupedCollections;
};
